<script setup lang="ts">
const speedDialStore = useSpeedDialStore()
const prestacaoContaStore = usePrestacaoDeContaStore()
const usuarioStore = useUsuarioStore()
const router = useRouter()

// Obter cor do cartório dinamicamente
const { usuarioAtual } = storeToRefs(usuarioStore)

const corBotaoPrincipal = computed(() => {
  return usuarioStore.getBgCor(usuarioAtual.value?.cartorio?.cor)
})

// Debug: verificar se o componente está sendo montado
onMounted(() => {
  console.log('SpeedDial montado com sucesso')
})

// Estado para versão fallback
const isOpen = ref(false)
const usePrimeSpeedDial = ref(false) // Começar com false para usar versão simples

const toggleMenu = () => {
  isOpen.value = !isOpen.value
}

const closeMenu = () => {
  isOpen.value = false
}

const getIconClass = (primeIcon: string) => {
  // Converter ícones Prime para FontAwesome
  const iconMap: Record<string, string> = {
    'pi pi-search': 'fas fa-search',
    'pi pi-plus': 'fas fa-plus',
    'pi pi-file': 'fas fa-file-invoice-dollar',
  }
  return iconMap[primeIcon] || 'fas fa-question'
}

const items = [
  {
    label: 'Visualizar Despesas',
    icon: 'pi pi-search',
    command: () => {
      speedDialStore.abrirModalDespesas()
      closeMenu()
    },
    className: 'speeddial-visualizar-despesas',
  },
  {
    label: 'Lançar Nova Despesa',
    icon: 'pi pi-plus',
    command: () => {
      prestacaoContaStore.modalLancarDespesa.exibir = true
      closeMenu()
    },
    className: 'speeddial-nova-despesa',
  },
  {
    label: 'Criar Novo Orçamento',
    icon: 'pi pi-file',
    command: () => {
      router.push('/orcamentos/novo')
      closeMenu()
    },
    className: 'speeddial-novo-orcamento',
  }
]
</script>

<template>
  <div class="speeddial-container">
    <!-- Versão fallback simples para debug -->
    <div v-if="!usePrimeSpeedDial" class="simple-speeddial">
      <div
        v-c-tooltip="'Ações rápidas'"
        class="main-button"
        :style="{ background: corBotaoPrincipal }"
        @click="toggleMenu"
      >
        <font-awesome-icon :icon="isOpen ? 'fas fa-times' : 'fas fa-bars'" />
      </div>

      <transition-group name="speeddial" tag="div" class="actions-container">
        <div
          v-for="(item, index) in items"
          v-show="isOpen"
          :key="item.label"
          v-c-tooltip="item.label"
          class="action-button"
          :style="{
            bottom: `${(index + 1) * 4.5}rem`,
            animationDelay: `${index * 50}ms`
          }"
          @click="item.command"
        >
          <font-awesome-icon :icon="getIconClass(item.icon)" />
        </div>
      </transition-group>

      <div v-if="isOpen" class="overlay" @click="closeMenu" />
    </div>

    <!-- Versão PrimeVue -->
    <PSpeedDial
      v-else
      :model="items"
      direction="up"
      button-class="p-button-primary p-button-rounded"
      show-icon="pi pi-bars"
      hide-icon="pi pi-times"
      :mask="true"
    />
  </div>
</template>

<style scoped>
.speeddial-container {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1050;
}

/* Versão simples do SpeedDial */
.simple-speeddial {
  position: relative;
}

.main-button {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  /* background removido - será aplicado dinamicamente via style inline */
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  position: relative;
  z-index: 2;
}

.main-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  filter: brightness(0.9);
}

.actions-container {
  position: absolute;
  bottom: 0;
  right: 0;
}

.action-button {
  position: absolute;
  right: 0;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  z-index: 2;
}

.action-button:nth-child(1) { background-color: #6f42c1; } /* Visualizar despesas */
.action-button:nth-child(2) { background-color: #198754; } /* Nova despesa */
.action-button:nth-child(3) { background-color: #0d6efd; } /* Novo orçamento */

.action-button:hover {
  transform: scale(1.05);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.25);
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: -1;
}

/* Animações */
.speeddial-enter-active, .speeddial-leave-active {
  transition: all 0.3s ease;
}

.speeddial-enter-from, .speeddial-leave-to {
  opacity: 0;
  transform: scale(0.5);
}

:deep(.p-speeddial-button) {
  width: 3.5rem !important;
  height: 3.5rem !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2) !important;
  transition: all 0.3s ease !important;
}

:deep(.p-speeddial-button:hover) {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3) !important;
}

/* Cores específicas para cada ação */
:deep(.speeddial-visualizar-despesas) {
  background-color: #6f42c1 !important;
}

:deep(.speeddial-nova-despesa) {
  background-color: #198754 !important;
}

:deep(.speeddial-novo-orcamento) {
  background-color: #0d6efd !important;
}

:deep(.p-speeddial-action) {
  width: 3.5rem !important;
  height: 3.5rem !important;
  border: none !important;
  border-radius: 50% !important;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2) !important;
  transition: all 0.3s ease !important;
}

:deep(.p-speeddial-action:hover) {
  transform: scale(1.1) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3) !important;
}

:deep(.p-speeddial-mask) {
  background-color: rgba(0, 0, 0, 0.4) !important;
}

/* Garantir que tooltips apareçam sobre o SpeedDial */
:global(.tooltip) {
  z-index: 10000 !important;
}

:global(.bs-tooltip-auto[data-popper-placement^="top"]),
:global(.bs-tooltip-top) {
  z-index: 10000 !important;
}

:global(.bs-tooltip-auto[data-popper-placement^="right"]),
:global(.bs-tooltip-end) {
  z-index: 10000 !important;
}

:global(.bs-tooltip-auto[data-popper-placement^="bottom"]),
:global(.bs-tooltip-bottom) {
  z-index: 10000 !important;
}

:global(.bs-tooltip-auto[data-popper-placement^="left"]),
:global(.bs-tooltip-start) {
  z-index: 10000 !important;
}

/* Estilo responsivo */
@media (max-width: 768px) {
  .speeddial-container {
    bottom: 1rem;
    right: 1rem;
  }

  .main-button {
    width: 3rem !important;
    height: 3rem !important;
  }

  .action-button {
    width: 3rem !important;
    height: 3rem !important;
  }

  :deep(.p-speeddial-button) {
    width: 3rem !important;
    height: 3rem !important;
  }

  :deep(.p-speeddial-action) {
    width: 3rem !important;
    height: 3rem !important;
  }
}
</style>
